import { AgxService, useGetPoisonQueueMessages } from '@urbanx/agx.support.client';
import {
  AgxButton,
  AgxColumn,
  AgxRow,
  AgxTable,
  AgxTypography,
  IColumnConfig,
  Icon_Arrow,
} from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SupportBackButton from '../SupportBackButton';

const tableColumns: (string | IColumnConfig)[] = ['Id', 'Inserted At', { name: 'Message Body' }];

const PoisonQueue = () => {
  const { service, queueName } = useParams<{
    service: AgxService;
    queueName: string;
  }>();

  const { data: queueMessages, isPending: isQueueMessagesPending } = useGetPoisonQueueMessages(
    service!,
    queueName!,
    !!service && !!queueName
  );

  const rows = useMemo(() => {
    if (!queueMessages) return [];

    return queueMessages.map((msg, idx) => ({
      Id: (
        <AgxTypography id={`poison-queue-message-id-${idx}`} type={'body_ui'} size={'small'}>
          <pre
            style={{
              fontSize: 'inherit',
              fontFamily: 'inherit',
            }}
          >
            {msg.id ?? '-'}
          </pre>
        </AgxTypography>
      ),
      'Inserted At': (
        <AgxTypography
          id={`poison-queue-message-insertedAt-${idx}`}
          type={'body_ui'}
          size={'small'}
        >
          <pre
            style={{
              fontSize: 'inherit',
              fontFamily: 'inherit',
            }}
          >
            {!msg.insertionTime ? '-' : dayjs(msg.insertionTime).format('DD/MM/YYYY h:mm A')}
          </pre>
        </AgxTypography>
      ),
      'Message Body': (
        <AgxTypography
          id={`poison-queue-message-messageBody-${idx}`}
          type={'body_ui'}
          size={'small'}
        >
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflow: 'hidden',
              WebkitLineClamp: 10,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              fontSize: 'inherit',
              fontFamily: 'inherit',
            }}
          >
            {msg.messageBodyJson ? JSON.stringify(JSON.parse(msg.messageBodyJson), null, 2) : '-'}
          </pre>
        </AgxTypography>
      ),
    }));
  }, [queueMessages]);

  return (
    <AgxColumn gap="spacing-xl" fill>
      <SupportBackButton />
      <Panel
        title={
          <AgxRow gap="spacing-sm" alignItems="alignCenter">
            <AgxButton
              id={'backToPoisonQueuesButton'}
              text={''}
              variant={'naked'}
              leftIcon={<Icon_Arrow direction={'left'} />}
              renderLink={(props) => <Link to="../" {...props} />}
              ariaLabel="Back to poison queues"
            />
            <AgxTypography id="poison-queue-title" type="title" size="large">
              Messages in Poison Queue - {service} - {queueName}
            </AgxTypography>
          </AgxRow>
        }
      >
        <AgxTable
          columns={tableColumns}
          rows={rows}
          renderRowLink={(index, linkProps) => {
            const message = queueMessages?.[index];
            if (!message?.id) return;
            return <Link {...linkProps} to={`./${message.id}`} aria-label={message.id} />;
          }}
          showLoadingState={isQueueMessagesPending}
          noDataLabel="Poison Queue is currently empty"
        />
      </Panel>
    </AgxColumn>
  );
};

export default PoisonQueue;
